import { WorkflowRun, WorkflowTemplate } from '@digital-twins/shared-types'

import {
  composeThreadStudyDoc,
  populationIdFromRunInputs,
  ThreadStudyDocBlock,
  ThreadStudyDocParts,
} from './composeThreadStudyDoc'
import { collectResultBlocks, toRenderableResult } from './ThreadRunResults'

/**
 * The template's audience input key. Lives here rather than on the page so
 * the run-scoped composition and the page's own settled-audience lookup read
 * the same definition: it was briefly copied into both, which is exactly how
 * the two drift.
 */
export function audienceInputKey(template: WorkflowTemplate): string | undefined {
  return template.inputs.find((spec) => spec.type === 'audience')?.key
}

/** One run's `quick_study` blocks plus the audience token stamped on it. */
export interface RunStudyDocBlocks {
  blocks: Array<ThreadStudyDocBlock>
  populationId?: string
}

/**
 * The per-run collector half of {@link composeRunStudyDoc}: everything up to
 * the point where blocks are shaped into a document.
 *
 * Split out for `composeThreadExhibits` (Step 8, D11), which needs the same
 * `collectResultBlocks` → `audienceInputKey` → `populationIdFromRunInputs`
 * prelude over several runs but composes a different, thread-wide result. It
 * is one function rather than two copies for the same reason
 * `audienceInputKey` lives here at all.
 *
 * `undefined` when there is no run, or the run produced no `quick_study`
 * blocks yet.
 */
export function collectRunStudyDocBlocks(
  run: WorkflowRun | null | undefined,
  runTemplate: WorkflowTemplate | null | undefined,
): RunStudyDocBlocks | undefined {
  if (!run) {
    return undefined
  }
  const blocks = collectResultBlocks(run)
  if (blocks.length === 0) {
    return undefined
  }
  const audienceKey = runTemplate
    ? (audienceInputKey(runTemplate) ?? 'audience')
    : 'audience'
  const populationId = populationIdFromRunInputs(run.inputs, audienceKey)
  return populationId ? { blocks, populationId } : { blocks }
}

/**
 * StudyDoc parts (plots / quotes / population id / strips) for ONE run
 * (Step 1c-ii, thread-output-continuity — closes D1).
 *
 * Extracted from `ThreadDetailPage.tsx`'s inline `studyDocParts` memo
 * (`:1268-1285`), which composes only the page's single CURRENT run. That
 * memo call is exactly `composeRunStudyDoc(run, runTemplate)` — same
 * `collectResultBlocks` → `audienceInputKey` → `populationIdFromRunInputs` →
 * `composeThreadStudyDoc` pipeline, same result shape. A thread with several
 * announced runs needs this per announcement, not once for the page, which is
 * why it is a plain function over `(run, template)` rather than a hook: the
 * run itself already comes from a hook (`useWorkflowRun` for the current run,
 * `useRunResults` for a past one), and this only shapes what that run
 * produced.
 *
 * `undefined` when there is no run, or the run produced no `quick_study`
 * blocks yet (matches the current inline behavior exactly).
 */
export function composeRunStudyDoc(
  run: WorkflowRun | null | undefined,
  runTemplate: WorkflowTemplate | null | undefined,
  segmentBy?: string | null,
): ThreadStudyDocParts | undefined {
  const collected = collectRunStudyDocBlocks(run, runTemplate)
  if (!collected) {
    return undefined
  }
  return composeThreadStudyDoc(
    collected.blocks,
    toRenderableResult,
    segmentBy ?? null,
    collected.populationId,
  )
}
